/**
 * <s-charm-solo>
 * "One charm, enormous" hero: a single charm fills the screen, then fades to the next like a perfume campaign.
 *   [data-slide]   each charm; the visible one carries data-active.
 *   [data-backdrop] one background scene per charm, in the same order; the current one carries data-active.
 *   [data-caption]  optional caption per charm kept outside the slides (e.g. under a window), same order; current gets data-active.
 *   [data-go]      buttons that jump to a slide (value = index); the current one gets aria-current="true".
 *   data-interval  milliseconds per charm (default 5500).
 *   data-keep-playing  keep auto-advancing while hovered or focused (clicking a [data-go] jumps there and carries on).
 * Otherwise pauses while hovered or focused. Never auto-advances for shoppers who prefer reduced motion.
 */
class SCharmSolo extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.dots = [...this.querySelectorAll('[data-go]')];
    this.backdrops = [...this.querySelectorAll('[data-backdrop]')];
    this.captions = [...this.querySelectorAll('[data-caption]')];
    if (this.slides.length < 2) return;
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.hasAttribute('data-active')));
    this.interval = Number(this.dataset.interval) || 5500;
    this.paused = false;

    this.handleClick = (event) => {
      const dot = event.target.closest('[data-go]');
      if (!dot) return;
      this.show(Number(dot.dataset.go));
      this.restart();
    };
    this.pause = () => {
      this.paused = true;
    };
    this.resume = () => {
      this.paused = false;
    };
    this.addEventListener('click', this.handleClick);
    if (!this.hasAttribute('data-keep-playing')) {
      this.addEventListener('pointerenter', this.pause);
      this.addEventListener('pointerleave', this.resume);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.resume);
    }

    this.show(this.index);
    this.restart();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('pointerenter', this.pause);
    this.removeEventListener('pointerleave', this.resume);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.resume);
  }

  restart() {
    clearInterval(this.timer);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.timer = setInterval(() => {
      if (!this.paused) this.show((this.index + 1) % this.slides.length);
    }, this.interval);
  }

  show(index) {
    this.index = index;
    this.slides.forEach((slide, i) => {
      slide.toggleAttribute('data-active', i === index);
      slide.toggleAttribute('inert', i !== index);
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    this.backdrops.forEach((backdrop, i) => backdrop.toggleAttribute('data-active', i === index));
    this.captions.forEach((caption, i) => caption.toggleAttribute('data-active', i === index));
  }
}

if (!customElements.get('s-charm-solo')) customElements.define('s-charm-solo', SCharmSolo);
